const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

class ApiError extends Error {
  constructor(
    public statusCode: number,
    message: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOptions extends RequestInit {
  params?: Record<string, string | number | boolean | undefined>;
  // Accepted for API signature compatibility — not sent as headers.
  // The backend resolves orgId/userId from the JWT token.
  orgId?: string;
  userId?: string;
}

// Token accessor injected by ApiClientInitializer (Clerk session token)
export type TokenAccessor = (options?: { skipCache?: boolean }) => Promise<string>;

let tokenAccessor: TokenAccessor | null = null;
let signedInAccessor: () => boolean = () => false;

/**
 * Set the token accessor function (called by ApiClientInitializer on mount)
 */
export function setTokenAccessor(fn: TokenAccessor) {
  tokenAccessor = fn;
}

/**
 * Set a function reporting whether Clerk currently has a signed-in session. When it does,
 * a 401 from the backend means the server rejected the session, not that the user must
 * sign in again, so the client must not redirect to /login (that would loop).
 */
export function setSignedInAccessor(fn: () => boolean) {
  signedInAccessor = fn;
}

// Global flag to prevent requests when authentication failed (redirecting to login)
let refreshFailed = false;

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

/**
 * Build the full URL for a backend request, normalizing the /api prefix.
 */
function buildUrl(
  endpoint: string,
  params?: Record<string, string | number | boolean | undefined>,
): string {
  const normalizedEndpoint = endpoint.startsWith('/api/') ? endpoint : `/api${endpoint}`;
  const url = new URL(`${API_BASE_URL}${normalizedEndpoint}`);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        url.searchParams.append(key, String(value));
      }
    });
  }
  return url.toString();
}

/**
 * Parse a fetch Response into the expected type, throwing ApiError on failure.
 */
async function parseResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({
      title: 'Error',
      status: response.status,
      detail: `Request failed with status ${response.status}`,
    }));
    const message = error.detail || error.message || 'Request failed';
    throw new ApiError(response.status, message, error);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  // Handle empty body (e.g., 201 with void return)
  const text = await response.text();
  if (!text) {
    return undefined as T;
  }

  return JSON.parse(text);
}

/**
 * Redirect to the login page and throw to halt execution.
 */
function redirectToLogin(): never {
  refreshFailed = true;
  try {
    localStorage.removeItem('auth-storage');
  } catch {
    // Ignore localStorage errors
  }
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
  // Throw to prevent further execution — the browser is navigating away
  throw new ApiError(401, 'Authentication failed - redirecting to login', {
    code: 'REDIRECT_TO_LOGIN',
  });
}

/**
 * Get a valid access token. Throws on failure instead of silently continuing.
 */
async function getAccessToken(): Promise<string | null> {
  if (refreshFailed) return null;
  if (!tokenAccessor) return null;

  try {
    return await tokenAccessor();
  } catch {
    // Token fetch failed — throw so callers get a clear error immediately
    // rather than making a doomed request that will 401.
    throw new ApiError(401, 'Failed to obtain access token', {
      code: 'TOKEN_FETCH_FAILED',
    });
  }
}

/**
 * The backend rejected a session that Clerk considers valid. Do not redirect (the login page
 * would bounce the user straight back); surface an error so the UI can offer a sign-out.
 */
function sessionRejected(): never {
  throw new ApiError(401, "We couldn't verify your session with the server", {
    code: 'SESSION_REJECTED',
  });
}

/**
 * Give up on a 401: redirect to login when the user is signed out, otherwise surface the error.
 */
function failUnauthorized(): never {
  if (signedInAccessor()) sessionRejected();
  redirectToLogin();
}

/**
 * Ask the token accessor for a fresh token (bypassing Clerk's cache) after a 401.
 */
async function refreshAccessToken(): Promise<string> {
  if (refreshFailed || !tokenAccessor) {
    redirectToLogin();
  }

  try {
    return await tokenAccessor({ skipCache: true });
  } catch {
    failUnauthorized();
  }
}

/**
 * Handle a 401 response by fetching a fresh token and retrying the request once.
 * If the retry is still unauthorized, redirect to login only when signed out.
 * Shared by both JSON and FormData request paths.
 */
async function handleRetryOn401<T>(retryFn: (token: string) => Promise<Response>): Promise<T> {
  const newToken = await refreshAccessToken();
  const retryResponse = await retryFn(newToken);
  if (retryResponse.status === 401) {
    failUnauthorized();
  }
  return parseResponse<T>(retryResponse);
}

// ---------------------------------------------------------------------------
// Request executors
// ---------------------------------------------------------------------------

/**
 * Execute a JSON request with automatic token refresh retry on 401 errors.
 */
async function executeRequest<T>(
  method: string,
  endpoint: string,
  options?: RequestOptions,
  body?: unknown,
): Promise<T> {
  if (refreshFailed) {
    throw new ApiError(401, 'Authentication failed - please login again', {
      code: 'REFRESH_FAILED',
    });
  }

  const fullUrl = buildUrl(endpoint, options?.params);
  const token = await getAccessToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const fetchOptions: RequestInit = {
    method,
    headers,
  };

  if (body !== undefined) {
    fetchOptions.body = JSON.stringify(body);
  }

  const response = await fetch(fullUrl, fetchOptions);

  if (response.status === 401 && !refreshFailed) {
    return handleRetryOn401<T>((newToken) =>
      fetch(fullUrl, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${newToken}` },
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      }),
    );
  }

  return parseResponse<T>(response);
}

/**
 * Execute a FormData request (for file uploads) with automatic 401 retry.
 */
async function executeFormDataRequest<T>(
  endpoint: string,
  formData: FormData,
  options?: RequestOptions,
): Promise<T> {
  if (refreshFailed) {
    throw new ApiError(401, 'Authentication failed - please login again', {
      code: 'REFRESH_FAILED',
    });
  }

  const fullUrl = buildUrl(endpoint, options?.params);
  const token = await getAccessToken();

  // No Content-Type — browser sets multipart boundary automatically
  const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};

  const response = await fetch(fullUrl, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (response.status === 401 && !refreshFailed) {
    return handleRetryOn401<T>((newToken) =>
      fetch(fullUrl, {
        method: 'POST',
        headers: { Authorization: `Bearer ${newToken}` },
        body: formData,
      }),
    );
  }

  return parseResponse<T>(response);
}

// ---------------------------------------------------------------------------
// Public API client
// ---------------------------------------------------------------------------

/**
 * Unified API client for SPA
 */
export const apiClient = {
  async get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return executeRequest<T>('GET', endpoint, options);
  },

  async post<T>(endpoint: string, data: unknown, options?: RequestOptions): Promise<T> {
    return executeRequest<T>('POST', endpoint, options, data);
  },

  async postForm<T>(endpoint: string, formData: FormData, options?: RequestOptions): Promise<T> {
    return executeFormDataRequest<T>(endpoint, formData, options);
  },

  async patch<T>(endpoint: string, data: unknown, options?: RequestOptions): Promise<T> {
    return executeRequest<T>('PATCH', endpoint, options, data);
  },

  async put<T>(endpoint: string, data: unknown, options?: RequestOptions): Promise<T> {
    return executeRequest<T>('PUT', endpoint, options, data);
  },

  async delete(endpoint: string, options?: RequestOptions): Promise<void> {
    return executeRequest<void>('DELETE', endpoint, options);
  },

  async deleteWithBody<T>(endpoint: string, data: unknown, options?: RequestOptions): Promise<T> {
    return executeRequest<T>('DELETE', endpoint, options, data);
  },
};

/**
 * Simple public API client that doesn't require authentication.
 */
export const publicApiClient = {
  async get<T>(endpoint: string): Promise<T> {
    const fullUrl = buildUrl(endpoint);
    const response = await fetch(fullUrl, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    return parseResponse<T>(response);
  },

  async post<T>(endpoint: string, data: unknown): Promise<T> {
    const fullUrl = buildUrl(endpoint);
    const response = await fetch(fullUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return parseResponse<T>(response);
  },
};

/**
 * Clear the token cache (call on logout)
 */
export function clearTokenCache(): void {
  refreshFailed = false;
}

export { ApiError };
